/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * @module dashboard/data/ThemeDefinition
 * @see ThemeDefinition
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../app/nls/StringResources',
	'underscore'
], function(Class, StringResources, _) {


	/*
	 * The purpose of this object is to hold the theme definition for a widget and provide
	 * easy access to elements of this definition. Please see public/themes/defaultTheme.json
	 * for more information of the current definition structure.
	 */
	let ThemeDefinition = null;
	const FOREGROUND_PALETTES = [
		'UserPaletteColor',
		'ForegroundUserPaletteColor',
		'SecondaryForegroundUserPaletteColor',
		'TabTextColor',
		'TabSelectedLineColor'
	];


	ThemeDefinition = Class.extend({
		init: function (themeDefinition) {
			// The actual JSON representing the theme
			this.jsonDefinition = themeDefinition;

			// The theme definitions may have any number of types of variables. For example,
			// there could be a group of variables represented fill colors in the 'Color'
			// group. There may also be a number of fonts in the 'Font' group, and so on. The
			// following will hold a map of each of these groups. For example, it will map 'Color'
			// to its corresponding mapping for each color in its group.
			this.variableMapping = {};

			// Some of our palettes use r,g,b,a. When creating styles dynamically
			// we need the css form rgba(r,g,b,a). Once created cache them
			this._cssColors = {};

			// Add the variable mappings
			this._mapVariables();

			this._fillColor = null;
		},

		getId() {
			return this.jsonDefinition.id;
		},

		getProperty(name) {
			return this.jsonDefinition && this.jsonDefinition[name];
		},

		/**
		 * Map the different group of variables ('Colors' and 'Fonts' for example)
		 * to their corresponding map of variables.
		 */
		_mapVariables: function () {
			if (this.jsonDefinition && this.jsonDefinition.variables) {
				const variables = this.jsonDefinition.variables;

				for (let variableType in variables) {
					if (variables.hasOwnProperty(variableType)) {
						this.variableMapping[variableType] = this._getMapOfVariables(variables[variableType]);
					}
				}
			}
		},

		/**
		 * Map each variable (such as 'color1' or 'color2' of a larger section (such as 'Color').
		 */
		_getMapOfVariables: function (variables) {
			let mapping = {};
			_.each(variables, function (variable) {
				mapping[variable.id] = variable;
			}.bind(this));
			return mapping;
		},

		/**
		 * Given the variable type (such as 'Color' or 'Font') and the variable ID
		 * (such as 'color1' or 'font1') return the value for the corresponding mapping
		 * @param variableType - Variable type (such as 'Color' or 'Font')
		 * @param variableId - id of a variable found within the specified variableType (such as 'color1')
		 * @returns value if the specified variable exists.
		 */
		getValueForVariable: function (variableType, variableId) {
			if (this.variableMapping[variableType]) {
				if (variableId) {
					if (this.variableMapping[variableType][variableId]) {
						return this.variableMapping[variableType][variableId].value;
					}
				} else {
					return this.variableMapping[variableType];
				}
			}
			return undefined;
		},
		/*
		 * return the value for the specified property. A value maybe defined with a leading '$' which
		 * means it should be replaced using its mapping.
		 */
		_getValueForProperty: function (prop) {
			if (prop && (prop.value !== undefined) && (prop.value !== null)) {
				// Some properties may have a number as a value and not point to a variable
				if (isNaN(prop.value) && prop.value.substr(0, 1) === '$') {
					return this.getValueForVariable(prop.type, prop.value.substr(1));
				}
				return prop.value;
			}
			return undefined;
		},

		getDefaultPaletteName: function (paletteType) {
			const keys = _.keys(this.variableMapping[paletteType]);
			if (keys && keys.length > 0) {
				return keys[0];
			}
			return undefined;
		},

		getColorValue: function (color) {
			if (color.pattern) {
				return color.pattern;
			} else if (color.fill) {
				return color.fill;
			} else if (color.backgroundColor) {
				return color.backgroundColor;
			}
			return color;
		},

		getCSSColors: function (variableName) {
			if (!this._cssColors[variableName]) {
				const foregroundColors = this._getVariable(variableName);
				if (!foregroundColors) {
					return {};
				}
				this._cssColors[variableName] = {};
				foregroundColors.forEach((color) => {
					this._cssColors[variableName][color.id] = this._convertToCssColor(color.value);
				});
			}

			return this._cssColors[variableName];
		},

		_convertToCssColor: function (color) {
			var cssColor = null;

			if (this._isColorValue(color)) {
				var value = color.r + ',' + color.g + ',' + color.b;

				cssColor = (color.a !== undefined) ? 'rgba(' + value + ',' + color.a + ')' :
					'rgb(' + value + ')';
			}
			return cssColor ? cssColor : color;
		},

		_isColorValue: function (color) {
			return (color && color.r !== undefined && color.g !== undefined && color.b !== undefined);
		},


		/**
		 * If a legacy theme is in use then we need to ensure that palettes are sorted
		 * in a way that results in the deprecated palettes to be after the new/modern
		 * palettes.
		 */
		_getVariable: function (variableName) {
			if (!this.jsonDefinition) {
				return null;
			}

			const { legacy, variables } = this.jsonDefinition;
			if (legacy === true) {
				return variables[variableName].sort(this._sortCarbonFirst);
			}

			return variables[variableName];
		},

		_sortCarbonFirst: function(a, b) {
			if (a.deprecated && !b.deprecated) {
				return 1;
			} else if (!a.deprecated && b.deprecated) {
				return -1;
			}
			return 0;
		},

		getPalette: function (paletteId, paletteVariableName, forProperties=false) {
			if (paletteId === 'userPaletteColor') {
				const userPaletteColor = this._getUserPaletteColor();
				return this._createCMPaletteResponse(userPaletteColor, forProperties);
			}

			const palettes = this._getVariable(paletteVariableName === 'UserPaletteColor' ? 'ColorPalette' : paletteVariableName);
			if (!palettes) {
				return {};
			}
			let carbonPaletteCount = 0;
			for (let i = 0; i < palettes.length; i++) {
				const palette = palettes[i];
				if (!palette.deprecated) {
					carbonPaletteCount++;
				}
				if (palette.id === paletteId || (palette.aliases && palette.aliases.includes(paletteId))) {
					const adjustedLegacyPaletteIndex = i - carbonPaletteCount;
					return this._createCMPaletteResponse(palette, forProperties, adjustedLegacyPaletteIndex);
				}
			}

			return this._createCMPaletteResponse(palettes[0], forProperties, 0);
		},

		_getUserPaletteColor: function () {
			const userPaletteColor = this._getVariable('UserPaletteColor');
			const result = {
				id: 'userPaletteColor',
				fillType: 'simple',
				label: StringResources.get('userPaletteColor_' + this.jsonDefinition.id),
				labelTranslated: true,
				ids: [],
				labels: [],
				fills: []
			};
			userPaletteColor.forEach(({ value, id, label }) => {
				result.fills.push(value);
				if (id) {
					result.ids.push(id);
				}
				if (label) {
					result.labels.push(label);
				}
			});

			return result;
		},

		/**
		 * Get all the palettes defined in the theme
		 * @param {string} paletteVariableName - the theme variable name for the list of palettes ('ColorPalette', 'HeatPalette', 'ContinuousPalette')
		 * @param {boolen} forProperties - boolean if the response is to be used for the properties
		 * @param {boolean} includePatternPalettes - Should pattern palettes be included in the list of returned palettes.
		 * @return {array} all palettes defined in the theme
		 */
		getPalettes: function(paletteVariableName, forProperties, includePatternPalettes = true) {
			const colorPalettes = this._getVariable(paletteVariableName);
			if (!colorPalettes) {
				return [];
			}
			let carbonPaletteCount = 0;
			const palettes = colorPalettes.filter((palette) => {
				return includePatternPalettes || _.find(palette.fills, (paletteProp) => {
					return !paletteProp.pattern;
				});
			}).map((palette, index) => {
				if (!palette.deprecated) {
					carbonPaletteCount++;
				}
				const adjustedLegacyPaletteIndex = index - carbonPaletteCount;
				return this._createCMPaletteResponse(palette, forProperties, adjustedLegacyPaletteIndex);
			});

			return palettes;
		},

		/**
		 * Make our system palettes have the same structure as the CM response
		 * @param {Object} palette
		 */
		_createCMPaletteResponse: function (palette, forProperties, index) {
			if (!palette) {
				return {};
			}
			const isPatternColor = _.find(palette.fills, (paletteProp) => {
				return paletteProp.pattern;
			});

			const label = palette.labelTranslated ? palette.label : StringResources.get(palette.label, { number: index + 1 });

			const content = _.extend(palette, {
				label: label,
				labelTranslated: true,
				fillType: isPatternColor ? 'ImgPath' : palette.fillType
			});
			if (isPatternColor) {
				content.path = 'dashboard-core/images/patterns/';
				content.fileType = '.svg';
			}

			// Note: CADBC-2142
			// We are modifying individual palette definitions directly here because
			// it is the only way for dashboard to be able to let our common UI control
			// (used in more places than just dashboard) to know how to render heat scale
			// palettes. Some need to be gradient and some need to be stepped.
			// Reporting currently doesn't want stepped.
			// The better option for this would be for the UI to handle how and not
			// the palette definition itself.
			if (forProperties) {
				if (!content.deprecated) {
					content.renderMode = 'step';
				}

				return {
					system: true,
					id: palette.id,
					content
				};
			}

			return content;
		},

		/**
		 * Get the property value for the UIElement and Property ID specified
		 * @param uiElement - UIElement as described in the theme
		 * @param propertyId - property within the specified UIElement
		 * @returns value for the property if it exists, undefined otherwise
		 */
		getValueForPropertyOfUIElement: function(uiElement, propertyId) {
			const prop = this.getPropertyForUIElement(uiElement, propertyId);
			return this._getValueForProperty(prop);
		},
		/**
		 * Get the property for the UIElement and Property ID specified
		 * @param uiElement - UIElement as described in the theme
		 * @param propertyId - property within the specified UIElement
		 * @returns property if it exists, undefined otherwise
		 */
		getPropertyForUIElement: function(uiElement, propertyId) {
			if (this.jsonDefinition && this.jsonDefinition.uiElements[uiElement]) {
				const uiElementArray = this.jsonDefinition.uiElements[uiElement];
				return _.find(uiElementArray, function(ele) {
					return ele.id === propertyId;
				}.bind(this));
			}
			return undefined;
		},

		getUIElement(uiElement) {
			return this.jsonDefinition.uiElements[uiElement];
		},

		getForegroundPropertiesForUIElement(uiElement) {
			const elements = this.jsonDefinition.uiElements[uiElement];
			if (!elements) {
				return [];
			}
			return elements.filter((element) => {
				return FOREGROUND_PALETTES.indexOf(element.type) !== -1;
			});
		},

		/*
		 * return array of id and foreground color pairs that are type is one of FOREGROUND_PALETTES in given elements
		 */
		getForegroundColorPropertiesForUIElement: function (bgColor, uiElement) {
			const result = [];
			const properties = this.getForegroundPropertiesForUIElement(uiElement);
			properties.forEach((ele) => {
				let fgColor = this._getValueForProperty({
					type: ele.type,
					value: '$' + bgColor
				});
				fgColor = (ele.alpha) ? this._convertToRGBA(fgColor, ele.alpha) : fgColor;
				result.push({
					id: ele.id,
					value: fgColor
				});
			});

			return result;
		},

		/*
		 * return an object with rgba properties for given color and alpha
		 */
		_convertToRGBA: function(color, alpha) {
			if (color) {
				let r = 0,
					g = 0,
					b = 0;
				if (typeof color === 'string') {
					if (color.charAt(0) === '#' && color.length === 7) {
						r = parseInt(color.substring(1, 3), 16);
						g = parseInt(color.substring(3, 5), 16);
						b = parseInt(color.substring(5), 16);
						return {
							'a': alpha,
							'r': r,
							'g': g,
							'b': b
						};
					}
				} else {
					return _.extend({
						'a': alpha
					}, _.omit(color, 'a'));
				}
			}
			return color;
		}
	});

	return ThemeDefinition;
});
